import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Tooltip } from 'antd';
import FlussonicMsePlayer from '@flussonic/flussonic-mse-player';
import cn from 'classnames';
import {
  cameraParentViews,
  controlBtnSizes,
  controlPanelPositions,
  controlPanelSizes,
  errorsMap,
  UNEXPECTED_ERROR,
} from './constants';
import Preview from './components/Preview/Preview';
import Root from './components/Root/Root';
import VideoContainer from './components/VideoContainer/VideoContainer';
import ControlBtn from './components/ControlBtn/ControlBtn';
import ControlPanel from './components/ControlPanel/ControlPanel';
import Error from './components/Error/Error';
import styles from './Camera.module.scss';

const Camera = ({
  data,
  parentView = cameraParentViews.table,
  onMenuClick,
  videoContainerId = 'videoContainerId',
  mainScreen = false,
  onSelectMainScreen,
  onEditName,
  selectMainScreenTooltip,
}) => {
  const videoRef = useRef();
  const playerRef = useRef(null);
  const isMountedRef = useRef(true);

  const [fullScreen, setFullScreen] = useState(false);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [showControlBtn, setShowControlBtn] = useState(false);

  const setStateIfMounted = useCallback((setState, value) => {
    if (isMountedRef.current) {
      setState(value);
    }
  }, []);

  const setErrorIfMounted = useCallback(
    (value) => {
      setStateIfMounted(setError, value);
    },
    [setStateIfMounted]
  );

  const play = useCallback(() => {
    const currentPlayer = playerRef.current;

    if (currentPlayer) {
      const playPromise = currentPlayer.play();

      if (playPromise) {
        setStateIfMounted(setLoading, true);

        playPromise
          .then(() => {
            if (!isMountedRef.current || playerRef.current !== currentPlayer) {
              return;
            }

            setLoading(false);
            setPlaying(true);
          })
          .catch(() => {
            if (!isMountedRef.current || playerRef.current !== currentPlayer) {
              return;
            }

            setLoading(false);
            setPlaying(false);
          });
      }
    }
  }, [setStateIfMounted]);

  const stop = useCallback(() => {
    const currentPlayer = playerRef.current;

    if (currentPlayer) {
      currentPlayer.stop();
    }

    if (!isMountedRef.current) {
      return;
    }

    setPlaying(false);
    setLoading(false);
  }, []);

  const fullScreenEnter = () => {
    setFullScreen(true);
  };

  const fullScreenExit = () => {
    setFullScreen(false);
  };

  const isPreview = useMemo(
    () => !fullScreen && !mainScreen,
    [fullScreen, mainScreen]
  );

  // Создаем плеер
  useEffect(() => {
    let newPlayer = null;
    let isCurrentPlayer = true;

    isMountedRef.current = true;

    const updateState = (setState, value) => {
      if (isMountedRef.current && isCurrentPlayer) {
        setState(value);
      }
    };

    if (videoRef.current) {
      const url = data.hlsUrl;
      const splitUrl = url.split('/');
      const host = splitUrl[2];
      const name = splitUrl[3];

      newPlayer = new FlussonicMsePlayer(
        videoRef.current,
        `wss://${host}/${name}/mse_ld`,
        {
          statsSendTime: 24 * 60,
          onError: (err) => {
            let errMsg = errorsMap.get(err?.error) || UNEXPECTED_ERROR;

            updateState(setError, errMsg);
            updateState(setLoading, false);
            updateState(setPlaying, false);
          },
          onStartStalling: () => {
            updateState(setLoading, true);
          },
          onEndStalling: () => {
            updateState(setLoading, false);
          },
        }
      );
    }

    playerRef.current = newPlayer;
    setPlayer(newPlayer);

    return () => {
      isCurrentPlayer = false;
      isMountedRef.current = false;

      if (newPlayer) {
        newPlayer.stop();
      }

      if (playerRef.current === newPlayer) {
        playerRef.current = null;
      }
    };
  }, [data.hlsUrl]);

  // Сбрасываем ошибки, если плеер проигрывается
  useEffect(() => {
    if (playing) {
      setError('');
    }
  }, [playing]);

  // Показываем или скрываем кнопку Старт/Пауза
  useEffect(() => {
    const show = player && (mainScreen || fullScreen);
    setShowControlBtn(show);
  }, [player, mainScreen, fullScreen]);

  // Останавливаем плеер
  useEffect(() => {
    const needStop = (playing && isPreview) || Boolean(error);

    if (needStop) {
      stop();
    }
  }, [playing, isPreview, error, stop]);

  //Автоматическим запускаем плеер, если он на главном экране
  useEffect(() => {
    if (mainScreen || fullScreen) {
      play();
    }
  }, [mainScreen, fullScreen, play]);

  const handleSelectMainScreen = () => {
    if (typeof onSelectMainScreen !== 'function') return;
    onSelectMainScreen();
  };

  const handleEditName = (event) => {
    event.stopPropagation();

    if (typeof onEditName !== 'function') return;
    onEditName(data);
  };

  return (
    <Root fullScreen={fullScreen}>
      <VideoContainer
        containerId={videoContainerId}
        fullScreen={fullScreen}
        setFullscreen={setFullScreen}
        className={styles.videoContainer}
        parentView={parentView}
        mainScreen={mainScreen}
        loading={loading}
        onOverlayClick={handleSelectMainScreen}
        overlayTooltip={selectMainScreenTooltip}
      >
        {!playing && !loading && (
          <Preview
            previewUrl={isPreview ? data.hlsUrlSec : data.hlsUrl}
            setError={setErrorIfMounted}
            error={error}
            className={cn(styles.video, {
              [styles.preview]: isPreview,
              [styles.fill]: mainScreen && !fullScreen,
            })}
          />
        )}

        <video
          className={cn(styles.video, {
            [styles.preview]: !fullScreen,
          })}
          ref={videoRef}
          preload="metadata"
          crossOrigin="anonymous"
          muted
        />

        <p
          className={styles.name}
          onClick={handleSelectMainScreen}
        >
          {data.name}
        </p>

        {typeof onEditName === 'function' && !fullScreen && (
          <Tooltip
            title="Изменить наименование"
            overlayStyle={{ zIndex: 20000 }}
          >
            <ControlBtn
              className={styles.editNameBtn}
              icon="edit"
              onClick={handleEditName}
              btnSize={controlBtnSizes.extraSmall}
            />
          </Tooltip>
        )}

        {showControlBtn && (
          <ControlBtn
            className={cn(styles.playPauseBtn, { [styles.hide]: playing })}
            onClick={playing ? stop : play}
            icon={playing ? 'pause-circle' : 'play-circle'}
            btnSize={controlBtnSizes.large}
          />
        )}

        {!isPreview && (
          <ControlPanel
            className={styles.hide}
            size={
              fullScreen ? controlPanelSizes.large : controlPanelSizes.medium
            }
            position={controlPanelPositions.bottom}
          >
            <Tooltip
              title={fullScreen ? 'Свернуть' : 'На весь экран'}
              overlayStyle={{ zIndex: 20000 }}
            >
              <ControlBtn
                className={styles.mlAuto}
                icon={fullScreen ? 'fullscreen-exit' : 'fullscreen'}
                onClick={fullScreen ? fullScreenExit : fullScreenEnter}
                btnSize={
                  fullScreen ? controlBtnSizes.medium : controlBtnSizes.small
                }
              />
            </Tooltip>
          </ControlPanel>
        )}
      </VideoContainer>

      <Error
        error={error}
        fullScreen={fullScreen}
      />
    </Root>
  );
};

export default Camera;
